import React from 'react';
import { Button, Card, Stack, TextField, Typography } from '@mui/material';
import { AccessTime, LocalTaxi } from '@mui/icons-material';
import { DeliveryType, isTableOrder, Order, OrderStatus } from '@api/Orders/Order';
import { Reward } from '@api/Rewards/Reward';
import OrderElapsedTime from '@components/Orders/OrderElapsedTime';
import OrderSummaryCard from '@screens/Orders/components/OrderSummaryCard';

interface OrderDetailPanelProps {
    order: Order;
    reward?: Reward;
    /** Solo el admin (perfil REAL) marca "Pagado" a mano un pedido de la web. */
    canMarkPaid: boolean;
    routeUrlDraft: string;
    onChangeRouteUrlDraft: (routeUrl: string) => void;
    onChangeStatusOrder: (newStatus: OrderStatus) => void;
    onConfirmCancelOrder: () => void;
    onSendWithUber: () => void;
    onRouteOrder: () => void;
    onConfirmCloseOrdersByTable: () => void;
}

/**
 * La columna del medio de la vista Lista: la tarjeta del pedido, entrega,
 * notas, el reloj y los botones que lo mueven de estado. Presentacional: cada
 * botón llama a una acción de `useOrderActions` (o de la navegación al POS);
 * aquí no se escribe nada. Los dos "Completar pedido" de este panel mandan
 * `Completed` a `onChangeStatusOrder`, que lo desvía al gate del cobro.
 */
const OrderDetailPanel: React.FunctionComponent<OrderDetailPanelProps> = ({
    order,
    reward,
    canMarkPaid,
    routeUrlDraft,
    onChangeRouteUrlDraft,
    onChangeStatusOrder,
    onConfirmCancelOrder,
    onSendWithUber,
    onRouteOrder,
    onConfirmCloseOrdersByTable
}) => {
    // Delivery listo que aún no va con Uber: aquí se decide cómo sale (Uber,
    // otra plataforma con link, o repartidor local sin link).
    const isManualDeliveryReady =
        !order.uberRouteUrl && order.deliveryType === DeliveryType.Delivery && order.status === OrderStatus.Ready;
    // Uber solo tiene sentido con el pedido pagado y aún en la sucursal: antes
    // el botón salía también en pedidos completados, cancelados, por pagar o
    // ya en ruta con otro repartidor.
    const canSendWithUber =
        !order.uberRouteUrl &&
        order.deliveryType === DeliveryType.Delivery &&
        [OrderStatus.Paid, OrderStatus.Preparing, OrderStatus.Ready].includes(order.status);

    return (
        <Stack spacing={2} m={2} mr={0}>
            {/* Tarjeta de información principal */}
            <OrderSummaryCard order={order} reward={reward} />

            {/* Tarjeta de dirección de envío (solo para delivery) */}
            {order.deliveryAddress && (
                <Stack component={Card} borderRadius={2} elevation={3} p={3} spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1" color="text.secondary">
                            Dirección de envío
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                                if (order?.deliveryAddress?.location) {
                                    const { lat, lng } = order?.deliveryAddress?.location;
                                    window.open(`https://www.google.com/maps/search/?api=1&query=${lat},${lng}`);
                                }
                            }}
                        >
                            Ver en mapa
                        </Button>
                    </Stack>
                    <Typography variant="body1">{order.deliveryAddress.address}</Typography>
                </Stack>
            )}

            <Stack component={Card} bgcolor="#FFFAE7" border="2px solid #D87F20" p={3}>
                <Typography variant="subtitle1" color="text.secondary">
                    Notas del pedido
                </Typography>
                <Typography variant="body1" fontStyle={!order.note ? 'italic' : 'inherit'}>
                    {order.note || 'Sin notas'}
                </Typography>
            </Stack>

            {order.deliveryType === DeliveryType.Delivery && order.routeUrl && !isManualDeliveryReady && (
                <Stack component={Card} borderRadius={2} elevation={3} p={3} spacing={1}>
                    <Typography variant="subtitle1" color="text.secondary">
                        {`Link de seguimiento: ${order.routeUrl}`}
                    </Typography>
                </Stack>
            )}

            <Stack component={Card} direction="row" justifyContent="space-between" alignItems="center" p={3}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <AccessTime color="primary" />
                    <Typography variant="subtitle1">Tiempo</Typography>
                </Stack>
                <OrderElapsedTime start={order.date.toDate()} status={order.status} duration={order.duration} />
            </Stack>

            {isManualDeliveryReady && (
                <Stack component={Card} borderRadius={2} elevation={3} p={3} spacing={1}>
                    <TextField
                        label="Link de seguimiento (opcional)"
                        value={routeUrlDraft}
                        onChange={(e) => onChangeRouteUrlDraft(e.target.value)}
                        helperText="Pégalo si el envío va con otra plataforma (Rappi, DiDi…). Con repartidor propio déjalo vacío."
                        fullWidth
                        multiline
                        rows={2}
                        variant="outlined"
                    />
                </Stack>
            )}

            {order.uberRouteUrl && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => {
                        window.open(order.uberRouteUrl);
                    }}
                    fullWidth
                    size="large"
                >
                    Rastreo Uber
                </Button>
            )}

            {!order.deliveryType &&
                order.status !== OrderStatus.Completed &&
                order.status !== OrderStatus.Cancelled && (
                    <Button variant="contained" color="error" onClick={onConfirmCancelOrder} fullWidth size="large">
                        Cancelar pedido
                    </Button>
                )}

            {order.deliveryType && order.status === OrderStatus.WaitingForPayment && canMarkPaid && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => onChangeStatusOrder(OrderStatus.Paid)}
                    fullWidth
                    size="large"
                >
                    Pagado
                </Button>
            )}

            {/* Preparing entra aquí porque el tablero de cocina mueve los
                pedidos de la web a "en preparación" antes de marcarlos listos */}
            {order.deliveryType && (order.status === OrderStatus.Paid || order.status === OrderStatus.Preparing) && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => onChangeStatusOrder(OrderStatus.Ready)}
                    fullWidth
                    size="large"
                >
                    Pedido Listo
                </Button>
            )}

            {canSendWithUber && (
                <Button
                    variant="contained"
                    sx={{ bgcolor: 'black', color: 'white', '&:hover': { bgcolor: '#333' } }}
                    onClick={onSendWithUber}
                    fullWidth
                    size="large"
                    startIcon={<LocalTaxi />}
                >
                    Enviar con Uber
                </Button>
            )}

            {isManualDeliveryReady && (
                <Button variant="contained" color="success" onClick={onRouteOrder} fullWidth size="large">
                    {routeUrlDraft.trim() ? 'En ruta con link' : 'En ruta (repartidor propio)'}
                </Button>
            )}

            {order.deliveryType === DeliveryType.Pickup && order.status === OrderStatus.Ready && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => onChangeStatusOrder(OrderStatus.Completed)}
                    fullWidth
                    size="large"
                >
                    Completar pedido
                </Button>
            )}

            {order.deliveryType && order.status === OrderStatus.Route && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => onChangeStatusOrder(OrderStatus.Completed)}
                    fullWidth
                    size="large"
                >
                    Completar pedido
                </Button>
            )}

            {isTableOrder(order) && order.status === OrderStatus.Preparing && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => onChangeStatusOrder(OrderStatus.Ready)}
                    fullWidth
                    size="large"
                >
                    Pedido listo
                </Button>
            )}

            {isTableOrder(order) && order.status === OrderStatus.Ready && (
                <Button
                    variant="contained"
                    color="success"
                    onClick={() => onConfirmCloseOrdersByTable()}
                    fullWidth
                    size="large"
                >
                    Cerrar mesa y pagar
                </Button>
            )}
        </Stack>
    );
};

export default OrderDetailPanel;
